import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { isVisaoAdministrador } from '@/shared/constants/visao-sistema.constants';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';

export type NavigationPermissions = {
  acoes: boolean;
  atuacao: boolean;
  comarca: boolean;
  defensores: boolean;
  defensoria: boolean;
  distritos: boolean;
  estagiarios: boolean;
  ferias: boolean;
  folhas: boolean;
  forum: boolean;
  colaboradores: boolean;
  logs: boolean;
  lotacoes: boolean;
  office: boolean;
  movimentos: boolean;
  nucleos: boolean;
  pessoas: boolean;
  cadastrosPessoas: boolean;
  regras: boolean;
  usuarios: boolean;
  vara: boolean;
  relatoriosAtividades: boolean;
  /** Aprovação na diretoria regional (`/relatorios/aprovacao`). */
  relatoriosDiretoria: boolean;
  /** Aval final na Corregedoria (`/relatorios/corregedoria`). */
  relatoriosCorregedoria: boolean;
  /** Log de alterações do sistema (`/changelog`). */
  changelog: boolean;
  /** Cadastro do Conselho Superior (`/conselho-superior`). */
  conselhoSuperior: boolean;
  /** Defensor com lotação diretor vigente (`/auth/me`). */
  ehDefensorDiretor: boolean;
  /** Visão autenticada de administrador. */
  administrador: boolean;
};

export function buildNavigationPermissions(
  relatoriosAtividadesHabilitado = true,
  ehDefensorDiretor = false,
  authVisao: NavigationView | null = null,
  assessorPodeEditarRelatorio = false,
): NavigationPermissions {
  const permissoes = CarregarPermissoes();
  const check = (role: RoleEnum, level: PermissionEnum) =>
    ValidarPermissao(permissoes, [{ role, level }]);
  const adminPorVisao = isVisaoAdministrador(authVisao);
  const suporteRelatorios =
    adminPorVisao || check(RoleEnum.ADMIN_ACTIONS, PermissionEnum.READ);
  const perfilRelatoriosDefensor =
    check(RoleEnum.DEFENSOR, PermissionEnum.READ) ||
    check(RoleEnum.COLABORADOR, PermissionEnum.READ) ||
    check(RoleEnum.MOVIMENTACAO, PermissionEnum.READ) ||
    check(RoleEnum.ATUACAO, PermissionEnum.READ) ||
    check(RoleEnum.SUBSTITUICAO, PermissionEnum.READ) ||
    check(RoleEnum.LOTACAO, PermissionEnum.READ);

  return {
    acoes: check(RoleEnum.MOVIMENTACAO, PermissionEnum.READ),
    atuacao: check(RoleEnum.ATUACAO, PermissionEnum.READ),
    comarca: check(RoleEnum.COMARCA, PermissionEnum.READ),
    defensores: check(RoleEnum.DEFENSOR, PermissionEnum.READ),
    defensoria: check(RoleEnum.DEFENSORIA, PermissionEnum.READ),
    distritos: check(RoleEnum.DISTRITO, PermissionEnum.READ),
    estagiarios: check(RoleEnum.ESTAGIARIO, PermissionEnum.READ),
    ferias: check(RoleEnum.LOTACAO, PermissionEnum.READ),
    folhas: check(RoleEnum.FOLHA, PermissionEnum.READ),
    forum: check(RoleEnum.PREDIO, PermissionEnum.READ),
    colaboradores: check(RoleEnum.COLABORADOR, PermissionEnum.READ),
    logs: check(RoleEnum.ADMIN_ACTIONS, PermissionEnum.READ),
    lotacoes: check(RoleEnum.LOTACAO, PermissionEnum.READ),
    office: check(RoleEnum.ADMIN_ACTIONS, PermissionEnum.READ),
    movimentos: check(RoleEnum.MOVIMENTACAO, PermissionEnum.READ),
    nucleos: check(RoleEnum.NUCLEO, PermissionEnum.READ),
    pessoas: check(RoleEnum.PESSOA, PermissionEnum.READ),
    cadastrosPessoas:
      check(RoleEnum.PESSOA, PermissionEnum.READ) ||
      check(RoleEnum.DEFENSOR, PermissionEnum.READ) ||
      check(RoleEnum.ESTAGIARIO, PermissionEnum.READ) ||
      check(RoleEnum.COLABORADOR, PermissionEnum.READ),
    regras: check(RoleEnum.GERENCIAR_REGRAS, PermissionEnum.READ),
    usuarios: check(RoleEnum.USUARIO, PermissionEnum.READ),
    vara: check(RoleEnum.VARA, PermissionEnum.READ),
    relatoriosAtividades:
      relatoriosAtividadesHabilitado &&
      authVisao !== NavigationView.Estagiario &&
      (suporteRelatorios || perfilRelatoriosDefensor || assessorPodeEditarRelatorio),
    relatoriosDiretoria:
      relatoriosAtividadesHabilitado && (ehDefensorDiretor || suporteRelatorios),
    relatoriosCorregedoria:
      relatoriosAtividadesHabilitado &&
      (authVisao === NavigationView.Corregedoria || suporteRelatorios),
    changelog:
      check(RoleEnum.CHANGELOG, PermissionEnum.READ) ||
      check(RoleEnum.ADMIN_ACTIONS, PermissionEnum.READ),
    conselhoSuperior: check(RoleEnum.CONSELHO_SUPERIOR, PermissionEnum.READ),
    ehDefensorDiretor,
    administrador: adminPorVisao,
  };
}
